/**
 * Created by mnaughton on 9/30/2015.
 */

/**
 * Front-end model representing a data type. See back-end complement for field descriptions.
 *
 * The following Field model attribute sets are located under the static `fields` attribute.
 * * name
 * @module app/models/Layer
 */
import Backbone from "backbone";
import _ from "underscore";
import FeatureCollection from "app/models/FeatureCollection";
import Field from "core/models/Field";
import ValueCollections from "app/models/ValueCollections";
import RequestUtils from "app/utils/RequestUtils";
import PermissionUtil from "@HBKEngineering/permission-util";
import _s from "underscore.string";
import DocumentCollection from "app/models/DocumentCollection";
import FieldCollection from "app/models/FieldCollection";
import ModelUtils from "@HBKEngineering/model-util";
import FieldUtils from "app/utils/FieldUtils";
import urlService from "../UrlService";
var Layer;

export default Layer = Backbone.Model.extend(
  {
    defaults: function () {
      return {
        hasGeometry: true,
        hasFiles: true,
        fieldSet: {},
      };
    },

    urlRoot: function () {
      return _.result(this.collection, "url", function () {
        return urlService.apiurl("/layers");
      });
    },

    constructor: function (attributes, options) {
      Backbone.Model.call(
        this,
        attributes,
        _.extend({}, options, {
          parse: true,
        })
      );
    },

    initialize: function (attributes, options) {
      Backbone.Model.prototype.initialize.call(this, attributes, options);

      this.options = _.defaults({}, options);
    },

    parse: function (attributes, options) {
      var results = Backbone.Model.prototype.parse.apply(this, arguments);

      if (results) {
        results.fieldSet = ModelUtils.idFor(results.fieldSet);
        results.nameField = ModelUtils.idFor(results.nameField);
      }

      return results;
    },

    /**
     * Archive the form.
     * @return {Promise} A promise object.
     * @async
     */
    archive: function () {
      return RequestUtils.post(this.url() + "/archive");
    },

    /**
     * Gets a value indicating whether or not entries on this layer can have geometries associated with them.
     * @return {Boolean} True if there is a geometry field, otherwise false.
     */
    isGeographical: function () {
      return this.attributes.hasGeometry;
    },

    /**
     * Gets a value indicating whether or not entries on this layer can have files associated with them.
     * @return {Boolean} True if files are supported, otherwise false.
     */
    hasFiles: function () {
      return this.attributes.hasFiles;
    },

    /**
     * Gets the collection of items within this data type.
     * @return {app/models/FeatureCollection} The collection items, if this model is not new, otherwise null.
     */
    getItems: function () {
      if (!this.isNew()) {
        if (!this._items) {
          this._items = new FeatureCollection(null, {
            layer: this,
          });
        }

        return this._items;
      } else {
        return null;
      }
    },

    /**
     * Gets the ID of the parent form of this form.
     * @return {string} The id.
     * @todo use ModelUtils here
     */
    getParentId: function () {
      var parent = this.get("parent");
      var result;

      if (_.isObject(parent)) {
        result = parent.id;
      } else {
        result = parent;
      }

      return result;
    },

    /**
     * Creates a set of Field models corresponding to each field in this layer.
     * @return {object} A mapping from field name to field model.
     */
    createFields: function () {
      return this.getFields().indexBy("label");
    },

    /**
     * Gets the set of fields associated with this data type.
     * @return {app/models/FieldCollection} The field set.
     */
    getFields: function () {
      if (!this._fields) {
        this._fields = new FieldCollection(null, {
          dataType: this,
        });
      }

      return this._fields;
    },

    /**
     * Gets the collection of documents associated with this feature.
     * @return {app/models/DocumentCollection} The document collection.
     */
    getDocuments: function () {
      if (!this._documents && !this.isNew()) {
        this._documents = Layer.getDocuments(this.id);
      }

      return this._documents;
    },

    /**
     * Gets the field which can be used to "name" data items of this type.
     * @return {app/Field} The name field.
     * @todo Use ModelUtils here
     */
    getNameField: function () {
      var nameFieldValue = this.get("nameField");

      if (_.isObject(nameFieldValue)) {
        nameFieldValue = nameFieldValue.id;
      }

      return this.getFields().find(function (field) {
        return nameFieldValue == field.id || nameFieldValue == field.cid;
      });
    },

    updateNameField: function (id, label) {
      if (this.nameField) {
        this.nameField.setListValue(id, label);
      }
    },

    /**
     * Creates a field for specifying the name field. Sorry that's confusing.
     * @param context The context to bind the nameFieldEventsTo.
     * @return {app/Field} The nameField field.
     */
    createNameFieldField: function () {
      if (this.nameField) {
        return this.nameField;
      }

      var values = this.getFields().map(function (field) {
        return {
          value: field.id,
          text: field.get("label"),
        };
      });

      var field = new Field({
        name: "nameField",
        label: "Primary Attribute (optional)",
        values: values,
      });

      this.nameField = field;

      //TODO also use default name text
      this.listenTo(this.getFields(), "add", function (_field) {
        field.addListValue(_field.get("label"), _field.id || _field.cid); //todo fix this warning
      });
      this.listenTo(this.getFields(), "remove", function (_field) {
        field.removeListValue(_field.id || _field.cid);
      });
      this.listenTo(this.getFields(), "change", function (_field) {
        field.setListValue(_field.id || _field.cid, _field.get("label"));
      });

      return field;
    },

    /**
     * Gets a value indicating whether or not the specified action is allowed for the current user.
     * @param session A reference to the current session.
     * @param  {string} action The action to check
     * @return {Boolean}        True if the action is allowed, otherwise false.
     */
    allows: function (session, action) {
      function userIsCreator(user, layer) {
        return ModelUtils.idFor(user) == ModelUtils.idFor(layer.get("owner"));
      }

      var result;

      if (session.hasRole("admin")) {
        result = true;
      } else {
        if (action == "create") {
          result = Layer.allowCreate(session);
        } else {
          /*	if they're not an admin, there are several places where we could find permission data
				 first, if there's an owning service, then we load its hash
				 if the current user owns the form, they can do whatever they want.
				 if the current user does not own the form, but they have permissions
				 set for the specific form, then we should use those permissons.
				 otherwise, we use the user's service-level permissions, if any.
				 specifically, we should be looking at permissions prefixed by "form:".
				 if a user owns the map service containing the form in question, and
				 they do not have form-level permissions set, then we act as if
				 they own the form, too*/
          var permSet = session.user.get("permissionSet");
          var serviceHash = {};
          var formRelatedServiceHash = {};
          var isLayerOwner = userIsCreator(session.user, this);
          var isServiceOwner;
          var layerHash = PermissionUtil.findActions(permSet, {
            DataType: this,
          });

          if (this.attributes.service) {
            serviceHash = PermissionUtil.findActions(permSet, {
              MapService: this.attributes.service,
            });
            serviceHash = serviceHash || {};
            isServiceOwner = serviceHash["own"];

            //remove any non-form-related service permissions
            var serviceKeys = _.keys(serviceHash);

            _.each(serviceKeys, function (serviceKey) {
              if (_s.startsWith(serviceKey, "form:")) {
                formRelatedServiceHash[_s.strRight(serviceKey, "form:")] =
                  serviceHash[serviceKey];
              }

              delete serviceHash[serviceKey];
            });
            //when we reach here, we'll have a service hash which can be directly combined
            //with other form permission hashes
          }
          var finalHash = PermissionUtil.inheritHashes(
            layerHash,
            formRelatedServiceHash
          );

          result = isLayerOwner || isServiceOwner || finalHash[action];
        }
      }

      return result;
    },

    save: function (attributes, options) {
      var self = this;
      //Get the name field of the model.
      //Is the name field a cId or an ID?
      //If it is not a cid ID, can just save as normal.
      var originalNameField = this.get("nameField");
      var nameField = self.getNameField();
      var cidRegex = /^c[0-9]{1,6}/;

      options = options || {};
      var success = options.success || function () {};
      var error = options.error || function () {};

      function saveSetNameField() {
        function saveNameField() {
          return nameField.save().then(function (savedField) {
            self.set("nameField", savedField.id);
          });
        }

        if (self.isNew()) {
          return Backbone.Model.prototype.save
            .apply(self, arguments)
            .then(saveNameField);
        } else {
          return saveNameField();
        }
      }

      function saveModelAndFields() {
        var deferred = new $.Deferred();
        var promise = Backbone.Model.prototype.save.apply(self, arguments);

        promise
          .done(function () {
            self
              .getFields()
              .save()
              .done(function () {
                deferred.resolve();
              })
              .fail(function (err) {
                deferred.reject(err);
              });
          })
          .fail(function (err) {
            deferred.reject(err);
          });

        deferred
          .done(function () {
            success(self, null, options);
          })
          .fail(function (err) {
            error(self, err, options);
          });

        return deferred.promise();
      }

      if (cidRegex.test(originalNameField) && nameField) {
        return saveSetNameField().then(saveModelAndFields);
      } else {
        return saveModelAndFields();
      }
    },

    /**
     * Gets a value indicating whether or not this layer supports exporting of data.
     * @returns {Boolean} True if export is supported, otherwise false.
     */
    canExport: function () {
      return true;
    },

    /**
     * Gets a value indicating whether or not this layer supports exporting of data to the specified format.
     * @returns {Boolean} True if export is supported, otherwise false.
     */
    canExportTo: function () {
      return true;
    },
  },
  {
    /**
     * Static function which can be used to determine whether or not the current user is authorized to create layers.
     * @param session A reference to the current session.
     * @param  {MapService} [service] The map service to which the form will be added, or null, if the form is not geographic.
     * @return {Boolean}         True if creation is allowed, otherwise false.
     * @todo use ModelUtils here
     */
    allowCreate: function (session, service) {
      var canCreate = session.hasRole("admin");
      var serviceId = _.isObject(service) ? service.id : service;

      if (serviceId) {
        var type = {
          MapService: serviceId,
        };

        if (
          session.can("own", type) ||
          (session.can("form:create", type) && session.can("update", type))
        ) {
          //if the service allows the user to create layers AND the user has access to modify the service
          if (session.can("create", "DataType") !== false) {
            //unless the user has been locked out of layer creation globally
            canCreate = true;
          } else {
            canCreate = false;
          }
        }
      } else {
        canCreate = session.can("create", "DataType"); //if the user has been allowed global layer creation
      }

      return canCreate;
    },

    /**
     * Gets the collection of documents associated with the layer with the given ID.
     * @param {String} layerId The ID of the layer
     * @return {app/models/DocumentCollection} The document collection.
     */
    getDocuments: function (layerId) {
      return new DocumentCollection(null, {
        url: Layer.getDocumentsUrl(layerId),
      });
    },

    /**
     * Gets the API endpoint for working with documents associated with the given layer.
     * @param {String} layerId The ID of the layer
     * @returns {String} The URL of the endpoint.
     */
    getDocumentsUrl: function (layerId) {
      return urlService.apiurl("/documents/bylayer/" + layerId);
    },

    fields: {
      name: FieldUtils.fieldPrototype("name", "Name", "text"),
      owner: FieldUtils.fieldPrototype("owner", "Owner", null, {
        editable: false,
        valueSource: ValueCollections.User,
      }),
      plugin: FieldUtils.fieldPrototype("plugin", "Registering Plugin", null, {
        editable: false,
        valueSource: ValueCollections.Plugin,
      }),
      showLabels: FieldUtils.fieldPrototype(
        "showLabels",
        "Display Labels",
        "checkbox"
      ),
    },
  }
);



//////////////////
// WEBPACK FOOTER
// ./public/js/app/models/Layer.js
// module id = 109
// module chunks = 1